import React, { FC, useEffect, useMemo, useState } from 'react';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import { DateRange } from '@material-ui/pickers';
import { useAppData, useLoading } from '../../../providers';
import { Header, PageTitle, QuickReportWithPercentage, ReportTitle } from '../../../components';
import { useStyles } from './styles';
import { PartnershipReports } from './components';
import useGetReports from './hooks/useGetReports';
import useSendEmail from './hooks/useSendEmail';
import { CommissionsByClientModel, CommissionsByClientReport } from './CommissionsByClientModel';
import { useCommonStyles } from '../../../styles';
import { ReportToolbarWithFilters } from './components/ReportToolbarWithFilters';
import { ColumnConfig, formatCurrency, formatNumber } from '../DataTable';

export const CommissionsByClient: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { userData, translations } = useAppData();
  const { setIsLoading } = useLoading();
  const [selectedDateRange, setSelectedDateRange] = React.useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().subtract(1, 'days').toDate()]);
  const [reportData, setReportData] = useState<CommissionsByClientModel>();
  const [showReports, setShowReports] = useState<boolean>(false);
  const [loginData, setLoginData] = useState<any>(null);
  const [translation, setTranslation] = useState<any>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState<string | undefined>(undefined);

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  useEffect(() => {
    const userLoginData = JSON.parse(userData);
    setLoginData(userLoginData);
  }, [userData]);

  const columns = useMemo((): ColumnConfig<CommissionsByClientReport>[] => {
    const first3: ColumnConfig<CommissionsByClientReport>[] = [
      { key: 'partnership', label: translation.text_6625 || '', type: 'string' },
      { key: 'clientName', label: translation.text_6635 || '', type: 'string' },
      { key: 'login', label: translation.text_6643 || '', type: 'number', format: (v) => (v ? v.toString() : '-') },
    ];

    const rest: ColumnConfig<CommissionsByClientReport>[] = [
      { key: 'rebateLocal', label: translation.text_6626 || '', type: 'number', format: (v) => formatCurrency(v), summable: true },
      { key: 'totalRebate', label: translation.text_6627 || '', type: 'number', format: (v) => formatCurrency(v), summable: true },
      { key: 'payment', label: translation.text_6628 || '', type: 'number', format: (v) => formatCurrency(v), summable: true },
      { key: 'profit', label: translation.text_6629 || '', type: 'number', format: (v) => formatCurrency(v), summable: true },
      { key: 'lots', label: translation.total_lots || 'Total Lots', type: 'number', format: (v) => formatNumber(v), summable: true },
      { key: 'paidCashback', label: translation.paid_cashback || '', type: 'number', format: (v) => formatCurrency(v), summable: true },
      { key: 'partneshipType', label: translation.text_6630 || '', type: 'string' },
      { key: 'scheme', label: translation.text_6631 || '', type: 'string' },
      { key: 'schemeType', label: translation.text_6632 || '', type: 'string' },
    ];

    if (groupBy === 'clientName') return [first3[1], first3[0], first3[2], ...rest];
    if (groupBy === 'login') return [first3[2], first3[0], first3[1], ...rest];
    return [...first3, ...rest];
  }, [translation, groupBy]);

  useEffect(() => {
    if (columns.length === 12) {
      setSelected(columns.slice(0, 8).map((col) => col.label));
    }
  }, [columns]);

  const handleNewReportRequest = useGetReports({
    dateRange: selectedDateRange,
    setReportData,
    showReports,
    setShowReports,
    setIsLoading,
  });

  const sendEmail = useSendEmail({
    dateRange: selectedDateRange,
    translation,
  });

  return (
    <>
      <Header title={translation.text_6663} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6663} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <ReportTitle
            title={translation.text_6450}
            subTitle={translation.commission_by_client_tooltip}
            exportFileName={translation.text_6450}
            columns={columns}
            allData={reportData?.items || []}
            selectedColumns={selected}
            printColumnNumber={selected.length}
            content={<PartnershipReports columns={columns} selectedColumns={selected} translation={translation} rows={reportData?.items || []} onSelectColumns={setSelected} groupByOption={groupBy} />}
            enableButtons={showReports}
            translation={translation}
            sendEmail={sendEmail}
            reportNameInEn="Commissions By Client"
          />
          <Grid container direction="column">
            <ReportToolbarWithFilters
              translation={translation}
              dateRange={selectedDateRange}
              handleDateRangeChange={(value) => setSelectedDateRange(value)}
              handleButtonClick={handleNewReportRequest}
              showGroupBy={reportData !== undefined}
              onGroupByChange={(option: string) => setGroupBy(option)}
            />
            {selectedDateRange[0] && (
              <Grid item xs={12}>
                <Grid container direction="row" spacing={2} className={classes.panels}>
                  <Grid item className={classes.quickReports}>
                    <QuickReportWithPercentage title={translation.text_6627} value={reportData?.total.rebateTotal || 0} percentage={reportData?.total.rebateTotalPercentage || 0} />
                  </Grid>
                  <Grid item className={classes.quickReports}>
                    <QuickReportWithPercentage title={translation.text_6628} value={reportData?.total.paymentTotal || 0} percentage={reportData?.total.paymentTotalPercentage || 0} />
                  </Grid>
                  <Grid item className={classes.quickReports}>
                    <QuickReportWithPercentage title={translation.text_6629} value={reportData?.total.profitTotal || 0} percentage={reportData?.total.profitTotalPercentage || 0} />
                  </Grid>
                  <Grid item className={classes.quickReports}>
                    <QuickReportWithPercentage title={translation.total_lots || 'Total Lots'} value={reportData?.total.lotsTotal || 0} percentage={reportData?.total.lotsTotalPercentage || 0} />
                  </Grid>
                </Grid>
                {loginData && <PartnershipReports columns={columns} translation={translation} selectedColumns={selected} rows={reportData?.items || []} onSelectColumns={setSelected} groupByOption={groupBy} />}
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
